/** 手机使用要求：记录按字段阅读，金额/薪资对比保留横向表；弹窗表单可换行、主要操作便于触控。只调整显示，不改变数据、权限及审批规则。 */
/**
 * 人才盘点 · 九宫格（Talent Review 9-Box）。
 *
 * 需求来源（2026-10-14 HR 反馈）：
 *   ① 「仅支持纵向移动，缺横向拖拽」——支持在 3×3 任意格子之间拖拽员工，拖到目标格即同时设定
 *      绩效轴(perfAxis) 与 文化/潜力轴(potential)，实现真正的二维移动；
 *   ② 「缺 50+50 十维评分表，评分口径不明」——新增十维评分（绩效 5 维 + 文化/协作 5 维，每维 0–10），
 *      按 HR 口径分档：绩效/文化各自 0–24 低、25–40 中、>40 高，自动定位到九宫格；
 *   ③ 「同事名单随机 / 缺失」——九宫格下方提供「全部同事名单」并支持按姓名/部门搜索，保证所有同事可被评估。
 *
 * 数据来源：GET /hrms/talent/grid（绩效轴来自十维评分 > KPI 综合分；文化/潜力轴来自潜力评估 > 十维文化分）；
 * 保存：PUT /hrms/talent/{eid}（可提交 potential / perfAxis / scores / note）。
 *
 * 2026-10-26（FBb0bfe541e7）：
 *   ① 文化/潜力纵轴原先只有定性文字，和带分数区间的绩效横轴不对称；
 *   ② HR 明确要求高绩效必须是「>40」，40 分本身只能算中档。
 *   因此两轴统一展示 50 分制区间，并使用严格大于 40 的高档阈值；后端同时下发两组
 *   评分合计，便于九宫格直接展示实际分数。手工拖拽/指定轴仍优先于评分推导，避免
 *   覆盖管理者已经确认的定位。
 */
import React, { useEffect, useState, useCallback } from "react";
import { useApp } from "../../context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "../../components/ui/sonner";
import { Loader2, UsersRound, Search, Wand2 } from "lucide-react";
// FBb0bfe541e7：页面用共享权限 hook 判定人才盘点查看权限；漏导入会在首次渲染时报错并使整页不可用。
// 这里只补齐已有 hook 的导入，不改变权限规则或评分口径。
import { DEPTS, DEPTS_EN, inp, useHrmsManage } from "./hrmsShared";

const POT = { high: "高潜力/高文化", mid: "中潜力/中文化", low: "低潜力/低文化" };
const POT_EN = { high: "High culture", mid: "Mid culture", low: "Low culture" };
const POT_RANGE = { high: ">40", mid: "25-40", low: "<25" };
const PERF = { high: "高绩效 >40", mid: "中绩效 25-40", low: "低绩效 <25" };
const PERF_EN = { high: "High perf >40", mid: "Mid perf 25-40", low: "Low perf <25" };
/**
 * 轴抬头写两条规则（一-5 补充，2026-10-26）。
 *
 * 改前痛点：抬头只写「高绩效 >40」，但绩效轴实际有四档来源
 * （手工 > 十维评分 > 岗位职责 > KPI 综合分）。生产实测 93 名在职员工里
 * 83 人走的最后一档 KPI（0–100 制），十维评分 0 人 —— 于是 KPI 88 分的人
 * 被排进「>40」那一列，看的人会以为他十维评分超过 40。
 * 修了什么：抬头把两套规则都写出来，让「这一列到底按什么算」不再需要猜。
 * 没改什么：判定结果与优先级完全不变，九宫格分布不动。
 */
const PERF_AXIS = {
  high: { label: "高绩效", rule: "十维 >40 · KPI ≥85" },
  mid: { label: "中绩效", rule: "十维 25-40 · KPI 70-84" },
  low: { label: "低绩效", rule: "十维 <25 · KPI <70" },
};
const PERF_AXIS_EN = {
  high: { label: "High", rule: "10-dim >40 · KPI ≥85" },
  mid: { label: "Mid", rule: "10-dim 25-40 · KPI 70-84" },
  low: { label: "Low", rule: "10-dim <25 · KPI <70" },
};
const BOX_CLS = {
  high_high: "bg-emerald-50 border-emerald-300", high_mid: "bg-emerald-50/60 border-emerald-200", low_high: "bg-amber-50 border-amber-300",
  mid_high: "bg-sky-50 border-sky-300", mid_mid: "bg-slate-50 border-slate-200", high_low: "bg-sky-50/60 border-sky-200",
  mid_low: "bg-slate-50 border-slate-200", low_mid: "bg-orange-50 border-orange-200", low_low: "bg-red-50 border-red-300",
};
const BOX_LABEL = {
  high_high: "超级明星", high_mid: "绩效之星", high_low: "中坚力量",
  mid_high: "潜力股", mid_mid: "核心骨干", mid_low: "稳定贡献",
  low_high: "待发展·需辅导", low_mid: "需改进", low_low: "风险人员",
};
const BOX_LABEL_EN = {
  high_high: "Superstar", high_mid: "Performance Star", high_low: "Solid Performer",
  mid_high: "Rising Talent", mid_mid: "Core Backbone", mid_low: "Steady Contributor",
  low_high: "Coach & Develop", low_mid: "Needs Improvement", low_low: "At Risk",
};
// 十维评分维度（绩效 5 维 + 文化/协作 5 维，每维 0–10）
const PERF_DIMS = [
  ["quality", "质量与准确性", "Quality & Accuracy"], ["productivity", "产出与结果", "Productivity / Results"],
  ["timeliness", "时效与可靠性", "Timeliness & Reliability"], ["knowledge", "岗位知识与胜任力", "Job Knowledge & Competency"],
  ["independence", "问题解决与独立性", "Problem Solving & Independence"],
];
const CULTURE_DIMS = [
  ["communication", "沟通", "Communication"], ["teamwork", "团队协作", "Teamwork & Collaboration"],
  ["accountability", "责任心", "Accountability"], ["adaptability", "适应力与反馈", "Adaptability & Feedback"],
  ["professionalism", "职业素养与公司标准", "Professionalism & Company Standards"],
];
// FBb0bfe541e7：高绩效口径是严格大于 40；40 分应留在中档。
const axisOf = (total) => (total > 40 ? "high" : total >= 25 ? "mid" : "low");
// 岗位职责逐条评分折算 0–50（与后端 _jd_total 同口径），用于对话框内实时显示
const jdTotalOf = (dlg) => {
  const duties = dlg?.duties || [];
  if (!duties.length) return null;
  const vals = duties.map((_, i) => parseFloat(dlg.jd?.[String(i)]) || 0);
  if (!vals.some((v) => v > 0)) return null;
  return Math.round((vals.reduce((a, b) => a + b, 0) / (duties.length * 10)) * 50 * 10) / 10;
};
const emptyScores = () => PERF_DIMS.concat(CULTURE_DIMS).reduce((o, [k]) => ({ ...o, [k]: "" }), {});

export default function HRMSTalent() {
  const { api, user, lang } = useApp();
  const zh = lang === "zh";
  const T = (z, e) => (zh ? z : e);
  const canView = useHrmsManage(user);
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  // 2026-10-14 补充（需求原文「A monthly/quarterly basis on these categories」）：支持「月度 / 季度」两种盘点粒度。
  const [gran, setGran] = useState("month");
  const [quarter, setQuarter] = useState(`Q${Math.floor(new Date().getMonth() / 3) + 1}`);
  const year = new Date().getFullYear();
  const effPeriod = gran === "quarter" ? `${year}-${quarter}` : period;
  const [data, setData] = useState(null);
  const [dlg, setDlg] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [overKey, setOverKey] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => {
    api.get("/hrms/talent/grid", { params: { period: effPeriod } }).then(({ data }) => setData(data))
      .catch((e) => { toast.error(e?.detailText || "Load failed"); setData({ rows: [], counts: {} }); });
  }, [api, effPeriod]);
  useEffect(() => { if (canView) reload(); }, [reload, canView]);

  if (!canView) return <div className="p-10 text-center text-slate-400 text-sm" data-testid="hrms-talent-denied">{T("仅 HR / 管理层 / 部门经理可访问人才盘点", "Talent review is limited to HR / management / department managers")}</div>;
  if (!data) return <div className="p-8 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;

  const saveTalent = async (eid, payload, msg) => {
    setBusy(true);
    try {
      await api.put(`/hrms/talent/${eid}`, payload);
      toast.success(msg); setDlg(null); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || T("保存失败", "Save failed")); }
    setBusy(false);
  };
  // 点击保存：提交潜力轴 + 绩效轴 + （若有）十维评分 + 说明
  const saveDlg = async () => {
    const body = { potential: dlg.newPot, perfAxis: dlg.newPerf, note: dlg.note || "" };
    if (dlg.useScores) body.scores = dlg.scores || {};
    if (dlg.useJd) body.jd = dlg.jd || {};
    await saveTalent(dlg.employeeId, body, T(`${dlg.name} 评估已保存`, `${dlg.name} assessment saved`));
  };
  // 拖拽落格：同时设定绩效轴 + 文化/潜力轴，实现二维移动（含横向）
  const onDropCell = async (pf, pot) => {
    setOverKey(null);
    if (!data.canAssess || !dragId) return;
    const row = data.rows.find((r) => r.employeeId === dragId);
    setDragId(null);
    if (!row || (row.perfAxis === pf && row.potential === pot)) return;
    await saveTalent(dragId, { perfAxis: pf, potential: pot, note: row.note || "" }, T(`${row.name} 已移动到「${BOX_LABEL[`${pf}_${pot}`]}」`, `${row.name} moved to "${BOX_LABEL_EN[`${pf}_${pot}`]}"`));
  };
  // 由十维评分推导两轴并回填到对话框
  const applyScores = () => {
    const s = dlg.scores || {};
    const ps = PERF_DIMS.reduce((a, [k]) => a + (parseFloat(s[k]) || 0), 0);
    const cs = CULTURE_DIMS.reduce((a, [k]) => a + (parseFloat(s[k]) || 0), 0);
    setDlg((d) => ({ ...d, newPerf: axisOf(ps), newPot: axisOf(cs), perfTotal: ps, cultureTotal: cs, useScores: true }));
  };

  const cell = (pf, pot) => data.rows.filter((r) => r.perfAxis === pf && r.potential === pot);
  const allRows = data.rows || [];
  const filtered = q ? allRows.filter((r) => (r.name || "").toLowerCase().includes(q.toLowerCase()) || (r.department || "").toLowerCase().includes(q.toLowerCase()) || (r.role || "").toLowerCase().includes(q.toLowerCase())) : allRows;
  // 定位依据（一-5 补充，2026-10-26）：显示「这一格凭什么判进来的」，而不是只显示综合分。
  //
  // 改前问题：格子和名单里只显示 composite（KPI 0–100 综合分），但把这个人放进哪一列
  // 可能根本不是靠 KPI —— 生产 93 人里 83 人靠 KPI、10 人手工、0 人十维评分。
  // 同一个 chip 上「列说 >40、数字说 78」这种自相矛盾，会让看的人无法核对。
  // 现在的口径：来源是十维就显示 x/50，是 KPI 就显示 KPI 分，手工就说明是手动的，
  // 潜力没评过就明确写「未评估」—— 不再让默认中档伪装成一次真实判断。
  // 注意：这里只影响展示，不参与任何判定。
  const perfLabel = (r) => {
    if (r.perfSource === "scores" && r.perfTotal != null) return T(`绩效 ${r.perfTotal}/50`, `P ${r.perfTotal}/50`);
    if (r.perfSource === "jd" && r.jdTotal != null) return T(`职责 ${r.jdTotal}/50`, `Duties ${r.jdTotal}/50`);
    if (r.perfSource === "kpi") return T(`KPI ${r.composite}`, `KPI ${r.composite}`);
    if (r.perfSource === "manual") return T("手动定位", "Manual");
    return "";
  };
  const potLabel = (r) => {
    if (r.potSource === "scores" && r.cultureTotal != null) return T(`文化 ${r.cultureTotal}/50`, `C ${r.cultureTotal}/50`);
    if (r.potSource === "manual") return T("手动定位", "Manual");
    if (r.potSource === "default") return T("潜力未评估", "Not assessed");
    return "";
  };
  const scoreSummary = (r) => [perfLabel(r), potLabel(r)].filter(Boolean).join(" · ");
  // 潜力是默认中档（从未评估）时给一个中性标记，避免和「评过、结论就是中档」混在一起。
  const potPending = (r) => r.potSource === "default";

  return (
    <div className="space-y-4" data-testid="hrms-talent-page">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="text-xl font-extrabold text-slate-800">{T("人才盘点 · 九宫格", "Talent Review · 9-Box")}</h1>
          <p className="text-xs text-slate-400 mt-0.5">{T("绩效（十维评分 / KPI 自动评分 × 文化·协作（十维评分 / 潜力评估））；支持任意格子拖拽（含横向）", "Performance × Culture/Collaboration; drag to any box (horizontal supported)")}</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <select data-testid="hrms-talent-gran" className="text-sm rounded-md border border-slate-200 px-2.5 py-1.5" value={gran} onChange={(e) => setGran(e.target.value)}>
            <option value="month">{T("月度", "Monthly")}</option>
            <option value="quarter">{T("季度", "Quarterly")}</option>
          </select>
          {gran === "month"
            ? <input data-testid="hrms-talent-period" type="month" className="text-sm rounded-md border border-slate-200 px-2.5 py-1.5" value={period} onChange={(e) => setPeriod(e.target.value)} />
            : <select data-testid="hrms-talent-quarter" className="text-sm rounded-md border border-slate-200 px-2.5 py-1.5" value={quarter} onChange={(e) => setQuarter(e.target.value)}>
                {[1, 2, 3, 4].map((n) => <option key={n} value={`Q${n}`}>{year} {T(`Q${n}`, `Q${n}`)}</option>)}
              </select>}
        </div>
      </div>

      {/* 九宫格的两个评价轴需要同时保留；手机提示横向滑动，点击员工仍可打开原有评分入口。 */}
      <p className="md:hidden text-xs text-slate-500">{T("左右滑动查看九宫格，点击员工查看或调整评价", "Swipe across the 9-box grid; tap an employee to view or adjust their review")}</p>
      <div className="max-w-full overflow-x-auto" tabIndex={0} role="region" aria-label={T("人才九宫格，可横向滚动", "Talent 9-box, horizontally scrollable")}>
        <div className="min-w-[760px]">
          <div className="grid gap-2" style={{ gridTemplateColumns: "80px repeat(3, 1fr)" }} data-testid="hrms-talent-grid"
            onPointerUp={() => { setDragId(null); setOverKey(null); }}>
            {["high", "mid", "low"].map((pot) => (
              <React.Fragment key={pot}>
                <div className="flex flex-col items-center justify-center text-center text-[11px] font-bold leading-tight text-slate-500" data-testid={`hrms-talent-pot-label-${pot}`}>
                  <span>{zh ? POT[pot] : POT_EN[pot]}</span>
                  <span className="mt-1 font-semibold text-slate-400">{POT_RANGE[pot]}</span>
                  {/* 一-5 补充：纵轴同样要说明「这不只是十维文化分」。生产 81/93 人的潜力是
                      从未评估过的默认中档，chip 上会标「未评估」，抬头这里给出判定口径。 */}
                  <span className="mt-0.5 font-normal text-slate-400">{T("潜力评估 · 或文化分", "Review · or culture")}</span>
                </div>
                {["low", "mid", "high"].map((pf) => {
                  const key = `${pf}_${pot}`;
                  const people = cell(pf, pot);
                  const over = overKey === key;
                  return (
                    <div key={key}
                      onDragOver={(e) => { if (data.canAssess) { e.preventDefault(); setOverKey(key); } }}
                      onDragLeave={() => setOverKey((k) => (k === key ? null : k))}
                      onDrop={() => onDropCell(pf, pot)}
                      onPointerMove={() => { if (dragId) setOverKey(key); }}
                      onPointerUp={() => { if (dragId) onDropCell(pf, pot); }}
                      className={`rounded-xl border p-2.5 min-h-[120px] ${BOX_CLS[key]} ${over && data.canAssess ? "ring-2 ring-brand-400" : ""}`} data-testid={`hrms-talent-box-${key}`}>
                      <p className="text-[11px] font-bold text-slate-600 mb-1.5">{zh ? BOX_LABEL[key] : BOX_LABEL_EN[key]}<span className="ml-1 text-slate-400 font-semibold">{people.length}</span></p>
                      <div className="flex flex-wrap gap-1">
                        {people.map((r) => (
                          <button key={r.employeeId} data-testid={`hrms-talent-chip-${r.employeeId}`}
                            draggable={data.canAssess}
                            onDragStart={() => setDragId(r.employeeId)}
                            onDragEnd={() => setDragId(null)}
                            onPointerDown={() => { if (data.canAssess) setDragId(r.employeeId); }}
                            onClick={() => data.canAssess && setDlg({ ...r, newPot: r.potential, newPerf: r.perfAxis, note: r.note, scores: { ...emptyScores(), ...(r.scores || {}) }, useScores: false, duties: r.duties || [], jd: { ...(r.jdScores || {}) }, useJd: false })}
                            className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-[11px] px-2 py-0.5 rounded-full bg-white/90 border text-slate-600 hover:border-slate-400 font-semibold whitespace-nowrap ${potPending(r) ? "border-dashed border-slate-300" : "border-slate-200"} ${data.canAssess ? "cursor-grab active:cursor-grabbing" : ""}`}
                            title={[T(`综合 ${r.composite} · ${r.grade} · 可拖拽到任意格子`, `Composite ${r.composite} · ${r.grade} · drag to any box`), scoreSummary(r)].filter(Boolean).join(" · ")}>
                            {/* chip 上显示真实依据的分数，而不是一律显示 composite：
                                十维评分显示 x/50、KPI 显示综合分、手工显示「手动」。
                                潜力从未评估的用虚线边框 + 「未评」标记，和「评过、结论中档」区分开。
                                「未评」用两字并配 whitespace-nowrap：1024 视口下写「未评估」会把
                                chip 挤成两行、把格子撑高，反倒看不出谁是未评估的。
                                见 perfLabel / potLabel 上方注释（一-5 补充，2026-10-26）。 */}
                            {r.name}
                            <span className="ml-1 text-slate-400">
                              {r.perfSource === "scores" && r.perfTotal != null ? `${r.perfTotal}` : r.perfSource === "jd" && r.jdTotal != null ? `${r.jdTotal}` : r.perfSource === "manual" ? T("手动", "M") : r.composite}
                            </span>
                            {potPending(r) && <span data-testid={`hrms-talent-pending-${r.employeeId}`} className="ml-1 text-[10px] font-normal text-slate-400">{T("未评", "N/A")}</span>}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
            <div />
            {["low", "mid", "high"].map((pf) => (
              /* 横轴抬头写两套规则（一-5 补充）：只写「>40」会让 KPI 回退的人被误读成十维超 40。
                 生产 83/93 人走 KPI 档，所以第二行规则不是补充说明，而是多数人的真实依据。 */
              <div key={pf} className="text-center text-[11px] leading-tight" data-testid={`hrms-talent-perf-label-${pf}`}>
                <div className="font-bold text-slate-500">
                  {(zh ? PERF_AXIS : PERF_AXIS_EN)[pf].label}
                  <span className="ml-1 font-semibold text-slate-400">{(zh ? PERF_AXIS : PERF_AXIS_EN)[pf].rule}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <p className="text-[11px] text-slate-400 flex items-center gap-1"><UsersRound className="h-3 w-3" />{T("纵轴=文化·协作（潜力），横轴=绩效；可把员工直接拖到任意格子（支持横向拖拽），或点击员工进入十维评分。", "Y = culture/collaboration, X = performance; drag a person to any box (horizontal supported) or click to open the 10-dimension scoring.")}</p>

      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="flex-wrap md:flex-nowrap flex items-center gap-2 px-3 py-2 border-b border-slate-100">
          <p className="text-xs font-bold text-slate-600">{T("全部同事名单", "All colleagues")}<span className="ml-1 text-slate-400 font-semibold">{allRows.length}</span></p>
          <div className="ml-auto flex items-center gap-1.5">
            <Search className="h-3.5 w-3.5 text-slate-400" />
            <input data-testid="hrms-talent-search" className="text-xs rounded border border-slate-200 px-2 py-1 w-48" placeholder={T("搜索姓名 / 部门 / 岗位", "Search name / dept / role")} value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
        </div>
        <div className="max-h-[280px] overflow-y-auto divide-y divide-slate-50">
          {filtered.length === 0 ? <p className="text-center py-6 text-slate-300 text-xs">{T("无匹配同事", "No matching colleagues")}</p> :
            filtered.map((r) => (
              <button key={r.employeeId} data-testid={`hrms-talent-list-${r.employeeId}`}
                onClick={() => data.canAssess && setDlg({ ...r, newPot: r.potential, newPerf: r.perfAxis, note: r.note, scores: { ...emptyScores(), ...(r.scores || {}) }, useScores: false, duties: r.duties || [], jd: { ...(r.jdScores || {}) }, useJd: false })}
                className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 w-full text-left px-3 py-2 flex items-center gap-2 text-xs hover:bg-slate-50">
                <span className="font-semibold text-slate-700 w-32 truncate">{r.name}</span>
                <span className="text-slate-400 w-28 truncate">{(zh ? DEPTS : DEPTS_EN)[r.department] || r.department} · {r.role}</span>
                <span className="text-slate-400">{T("综合", "Composite")} {r.composite}</span>
                {scoreSummary(r) && <span data-testid={`hrms-talent-score-summary-${r.employeeId}`} className="text-slate-400 truncate">{scoreSummary(r)}</span>}
                {/* 一-5 补充：名单里也把「潜力未评估」标出来，否则这 81 人与真正评为中档的人看起来完全一样。
                    scoreSummary 里已含 potLabel（未评估时会写「潜力未评估」），这里只在它没覆盖到时补标记，
                    避免同一行重复出现两次同样的字样。 */}
                {potPending(r) && !potLabel(r) && <span data-testid={`hrms-talent-list-pending-${r.employeeId}`} className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 shrink-0">{T("潜力未评估", "Unassessed")}</span>}
                <span className="ml-auto text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-semibold">{zh ? BOX_LABEL[`${r.perfAxis}_${r.potential}`] : BOX_LABEL_EN[`${r.perfAxis}_${r.potential}`]}</span>
              </button>
            ))}
        </div>
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{T("人才评估", "Talent Assessment")} · {dlg?.name}</DialogTitle></DialogHeader>
          {dlg && (
            <div className="space-y-3 text-sm" data-testid="hrms-talent-dlg">
              <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-500 space-y-1">
                <p className="flex justify-between">{T("部门 / 岗位", "Dept / Role")}<b className="text-slate-700">{(zh ? DEPTS : DEPTS_EN)[dlg.department] || dlg.department} · {dlg.role}</b></p>
                <p className="flex justify-between">{T("当期综合分 / 评级", "Composite / Grade")}<b className="text-slate-700">{dlg.composite} · {dlg.grade}</b></p>
                <p className="flex justify-between">{T("当前定位", "Current box")}<b className="text-slate-700">{dlg.box}</b></p>
                {/* 一-5 补充：把定位依据摊开写。原先只显示「当前定位 = 某格」，
                    但这一格可能来自手工拖动、十维评分、岗位职责或 KPI 综合分，四种依据
                    完全不同；不写清依据，管理者无法判断这个定位该不该改。 */}
                <p className="flex justify-between" data-testid="hrms-talent-dlg-basis">
                  {T("定位依据", "Placement basis")}
                  <b className="text-slate-700 text-right">{[
                    dlg.perfSource === "manual" ? T("绩效：手动指定", "Perf: manual") : dlg.perfSource === "scores" ? T(`绩效：十维 ${dlg.perfTotal ?? "-"}/50`, `Perf: 10-dim ${dlg.perfTotal ?? "-"}/50`) : dlg.perfSource === "jd" ? T(`绩效：岗位职责 ${dlg.jdTotal ?? "-"}/50`, `Perf: duties ${dlg.jdTotal ?? "-"}/50`) : T(`绩效：KPI 综合分 ${dlg.composite}`, `Perf: KPI ${dlg.composite}`),
                    dlg.potSource === "manual" ? T("潜力：手动指定", "Potential: manual") : dlg.potSource === "scores" ? T(`潜力：文化分 ${dlg.cultureTotal ?? "-"}/50`, `Potential: culture ${dlg.cultureTotal ?? "-"}/50`) : T("潜力：未评估（默认中档）", "Potential: not assessed (defaults to mid)"),
                  ].join(" · ")}</b>
                </p>
                {dlg.assessedBy && <p className="flex justify-between">{T("上次评估人", "Last assessed by")}<b className="text-slate-700">{dlg.assessedBy}</b></p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block text-xs text-slate-500">{T("绩效轴（横轴）", "Performance (X)")}
                  <div className="flex gap-1.5 mt-1">
                    {["low", "mid", "high"].map((p) => (
                      <button key={p} data-testid={`hrms-talent-perf-${p}`} onClick={() => setDlg((d) => ({ ...d, newPerf: p }))}
                        className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-[11px] font-bold rounded-lg px-3 py-1.5 border ${dlg.newPerf === p ? "border-slate-800 bg-slate-800 text-white" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>{zh ? PERF[p] : PERF_EN[p]}</button>
                    ))}
                  </div>
                </label>
                <label className="block text-xs text-slate-500">{T("文化/潜力轴（纵轴）", "Culture/Potential (Y)")}
                  <div className="flex gap-1.5 mt-1">
                    {["low", "mid", "high"].map((p) => (
                      <button key={p} data-testid={`hrms-talent-pot-${p}`} onClick={() => setDlg((d) => ({ ...d, newPot: p }))}
                        className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-[11px] font-bold rounded-lg px-3 py-1.5 border ${dlg.newPot === p ? "border-slate-800 bg-slate-800 text-white" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>{`${zh ? POT[p] : POT_EN[p]} ${POT_RANGE[p]}`}</button>
                    ))}
                  </div>
                </label>
              </div>

              <div className="rounded-lg border border-slate-200 p-2.5">
                <div className="flex-wrap md:flex-nowrap flex items-center justify-between mb-1.5">
                  <p className="text-xs font-bold text-slate-600">{T("十维评分表（每维 0–10；绩效 50 分 + 文化/协作 50 分）", "10-Dimension Scorecard (0–10 each; 50 performance + 50 culture)")}</p>
                  <button data-testid="hrms-talent-apply-scores" onClick={applyScores} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1 text-[11px] text-indigo-600 font-semibold"><Wand2 className="h-3 w-3" />{T("按评分自动定位", "Auto-place by scores")}</button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
                  <div>
                    <p className="text-[11px] text-slate-400 font-semibold mt-1 mb-0.5">{T("绩效 50 分", "Performance 50")} <b className={dlg.perfTotal != null && dlg.perfTotal > 40 ? "text-emerald-600" : "text-slate-500"}>{dlg.perfTotal != null ? `${dlg.perfTotal}/50` : ""}</b></p>
                    {PERF_DIMS.map(([k, z, en]) => (
                      <label key={k} className="flex items-center justify-between gap-2 text-[11px] text-slate-500 py-0.5">{zh ? z : en}
                        <input data-testid={`hrms-talent-score-${k}`} type="number" min="0" max="10" step="1" className="w-16 text-xs rounded border border-slate-200 px-1.5 py-1 text-right" value={dlg.scores?.[k] ?? ""}
                          onChange={(e) => setDlg((d) => ({ ...d, useScores: true, scores: { ...(d.scores || {}), [k]: e.target.value } }))} />
                      </label>
                    ))}
                  </div>
                  <div>
                    <p className="text-[11px] text-slate-400 font-semibold mt-1 mb-0.5">{T("文化/协作 50 分", "Culture 50")} <b className={dlg.cultureTotal != null && dlg.cultureTotal > 40 ? "text-emerald-600" : "text-slate-500"}>{dlg.cultureTotal != null ? `${dlg.cultureTotal}/50` : ""}</b></p>
                    {CULTURE_DIMS.map(([k, z, en]) => (
                      <label key={k} className="flex items-center justify-between gap-2 text-[11px] text-slate-500 py-0.5">{zh ? z : en}
                        <input data-testid={`hrms-talent-score-${k}`} type="number" min="0" max="10" step="1" className="w-16 text-xs rounded border border-slate-200 px-1.5 py-1 text-right" value={dlg.scores?.[k] ?? ""}
                          onChange={(e) => setDlg((d) => ({ ...d, useScores: true, scores: { ...(d.scores || {}), [k]: e.target.value } }))} />
                      </label>
                    ))}
                  </div>
                </div>
                {dlg.useScores && <p className="text-[11px] text-amber-600 mt-1">{T("保存时将一并提交十维评分；若上方两轴已手动选择，则以手动选择为准。", "Scores will be saved together; manually selected axes take precedence.")}</p>}
                {/* 评分档说明表（对应需求原文「Rating Scale for Each Factor」） */}
                <div className="mt-2 rounded-md border border-slate-100 bg-slate-50/60 p-2">
                  <p className="text-[11px] font-semibold text-slate-500 mb-0.5">{T("评分档说明（每维）", "Rating scale (per factor)")}</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-[11px] text-slate-500">
                    <span>9–10 · {T("持续超出预期", "Consistently exceeds expectations")}</span>
                    <span>7–8 · {T("稳定达标", "Consistently meets expectations")}</span>
                    <span>5–6 · {T("部分达标 / 不稳定", "Partially meets / inconsistent")}</span>
                    <span>3–4 · {T("经常低于预期", "Frequently below expectations")}</span>
                    <span>0–2 · {T("显著低于预期", "Significantly below expectations")}</span>
                  </div>
                </div>
              </div>

              {/* 岗位职责（JD）逐条评估（对应需求「按岗位职责做评估细分」） */}
              {(dlg.duties || []).length > 0 && (
                <div className="rounded-lg border border-slate-200 p-2.5" data-testid="hrms-talent-jd-box">
                  <div className="flex-wrap md:flex-nowrap flex items-center justify-between mb-1.5">
                    <p className="text-xs font-bold text-slate-600">{T("岗位职责达成（按 JD 逐条评分，每项 0–10）", "Job-duty breakdown (score each responsibility 0–10)")}</p>
                    <span className="text-[11px] text-slate-400">{T("折算", "Scaled")} <b className={(jdTotalOf(dlg) || 0) > 40 ? "text-emerald-600" : "text-slate-500"}>{jdTotalOf(dlg) != null ? `${jdTotalOf(dlg)}/50` : "—"}</b></span>
                  </div>
                  <div className="space-y-0.5">
                    {dlg.duties.map((duty, i) => (
                      <label key={i} className="flex items-center justify-between gap-2 text-[11px] text-slate-500 py-0.5">
                        <span className="flex-1">{i + 1}. {duty}</span>
                        <input data-testid={`hrms-talent-jd-${i}`} type="number" min="0" max="10" step="1" className="w-16 text-xs rounded border border-slate-200 px-1.5 py-1 text-right"
                          value={dlg.jd?.[String(i)] ?? ""}
                          onChange={(e) => setDlg((d) => ({ ...d, useJd: true, jd: { ...(d.jd || {}), [String(i)]: e.target.value } }))} />
                      </label>
                    ))}
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1">{T("来源：该岗位的职责条目（角色目录 / 部门手册）。未手动选择绩效轴时，将按职责合计自动定位。", "Source: the role's job responsibilities (role catalog / dept handbook). If no performance axis is picked, it auto-derives from this total.")}</p>
                </div>
              )}

              <label className="block text-xs text-slate-500">{T("评估说明", "Notes")}
                <textarea data-testid="hrms-talent-note" rows={3} className={inp} value={dlg.note} onChange={(e) => setDlg((d) => ({ ...d, note: e.target.value }))} placeholder={T("发展建议 / 接班人计划 / 培养方向…", "Development advice / succession plan / growth path…")} />
              </label>
              <div className="flex-wrap md:flex-nowrap flex justify-end gap-2">
                <button onClick={() => setDlg(null)} className="text-xs rounded-full border border-slate-300 text-slate-500 font-semibold px-4 py-2">{T("取消", "Cancel")}</button>
                <button data-testid="hrms-talent-save" disabled={busy} onClick={saveDlg} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs rounded-full bg-brand-500 hover:bg-brand-600 text-white font-semibold px-4 py-2 disabled:opacity-50">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin inline" /> : T("保存评估", "Save")}</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
